import React, { useState, useEffect } from 'react'
import { MdAdd, MdSearch, MdSend } from 'react-icons/md'
import { GoPencil } from 'react-icons/go'
import { CgEye } from 'react-icons/cg'
import {
    getPolicies,
    publishPolicy,
    unpublishPolicy,
    sendToAnalysis,
    deletePolicy,
} from '@/lib/api/policies/policies-api'
import {
    Policy,
    PolicyStatus,
    PolicyType,
} from '@/lib/api/common/interfaces/policy.interface'
import { ApiResponsePagination } from '@/lib/api/request'
import ConfirmModal from '@/components/confirm-modal'
import PolicyTypeModal from '@/components/policy-type-modal'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

const getPolicyTypeLabel = (type: PolicyType) => {
    const typeMap: Record<PolicyType, string> = {
        TERMS_OF_SERVICE: 'Termos de Uso',
        PRIVACY_POLICY: 'Política de Privacidade',
        COOKIE_POLICY: 'Política de Cookies',
        LGPD_PRIVACY_NOTICE: 'Aviso de Privacidade LGPD',
        GDPR_POLICY: 'Política GDPR',
        CCPA_POLICY: 'Política CCPA',
        DATA_CONSENT_DECLARATION: 'Declaração de Consentimento',
        NDA: 'Acordo de Confidencialidade',
        SECURITY_POLICY: 'Política de Segurança',
        SERVICE_CONTRACT: 'Contrato de Serviço',
        CODE_OF_CONDUCT: 'Código de Conduta',
        DATA_RETENTION_DISPOSAL_POLICY: 'Política de Retenção de Dados',
        REFUND_POLICY: 'Política de Reembolso',
        RETURN_POLICY: 'Política de Devolução',
        SHIPPING_POLICY: 'Política de Envio',
        CANCELLATION_POLICY: 'Política de Cancelamento',
        PAYMENT_POLICY: 'Política de Pagamento',
        SUBSCRIPTION_POLICY: 'Política de Assinaturas',
        ACCEPTABLE_USE_POLICY: 'Política de Uso Aceitável',
        COMMUNITY_GUIDELINES: 'Diretrizes da Comunidade',
        DISCLAIMER: 'Aviso Legal',
        END_USER_LICENSE_AGREEMENT: 'EULA',
        COPYRIGHT_POLICY: 'Política de Direitos Autorais',
        TRADEMARK_POLICY: 'Política de Marcas',
        THIRD_PARTY_POLICY: 'Política de Terceiros',
        MARKETING_POLICY: 'Política de Marketing',
        EMPLOYEE_PRIVACY_POLICY: 'Privacidade de Funcionários',
        INTERNAL_USE_POLICY: 'Política de Uso Interno',
        OTHER: 'Outro',
    }
    return typeMap[type] || type
}

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
}

const formatVersion = (version: number) => {
    const versionStr = version.toString()
    if (versionStr.length === 1) {
        return `0.${versionStr}`
    }
    const majorVersion = versionStr.slice(0, -1) || '0'
    const minorVersion = versionStr.slice(-1)
    return `${majorVersion}.${minorVersion}`
}

const getStatusBadge = (status: PolicyStatus) => {
    const statusMap = {
        PUBLISHED: { label: 'Publicado', bg: '#DEF0DE', color: '#5CB85C' },
        DRAFT: { label: 'Rascunho', bg: '#FFF4E5', color: '#FF9800' },
        IN_ANALYSIS: {
            label: 'Revisão jurídica',
            bg: '#E3F0F9',
            color: '#2196F3',
        },
        INACTIVE: { label: 'Inativo', bg: '#E8E8E8', color: '#666666' },
    }
    const statusInfo = statusMap[status] || {
        label: status,
        bg: '#F3F4F6',
        color: '#6B7280',
    }

    return (
        <span
            className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium"
            style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}
        >
            {statusInfo.label}
        </span>
    )
}

export default function PoliciesListPage() {
    const navigate = useNavigate()
    const [policies, setPolicies] = useState<Policy[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [pagination, setPagination] = useState<ApiResponsePagination | null>(
        null
    )
    const [showPreviewModal, setShowPreviewModal] = useState(false)
    const [previewContent, setPreviewContent] = useState('')
    const [selectedPreviewPolicy, setSelectedPreviewPolicy] =
        useState<Policy | null>(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showAnalysisModal, setShowAnalysisModal] = useState(false)
    const [showPolicyTypeModal, setShowPolicyTypeModal] = useState(false)
    const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [publishing, setPublishing] = useState<number | null>(null)
    const [sendingToAnalysis, setSendingToAnalysis] = useState(false)

    const fetchPolicies = async (page: number = 1) => {
        try {
            setLoading(true)
            const response = await getPolicies({
                page,
                limit: 15,
                search: searchTerm || undefined,
            })

            if (response.success && response.result) {
                setPolicies(response.result)
                if (response.pagination) {
                    setPagination(response.pagination)
                }
            }
        } catch (error) {
            console.error('Erro ao buscar políticas:', error)
            toast.error('Erro ao buscar políticas')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchPolicies(currentPage)
    }, [currentPage, searchTerm])

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        setCurrentPage(1)
    }

    const handlePublishToggle = async (policy: Policy) => {
        try {
            setPublishing(policy.id)

            let response
            if (policy.status === PolicyStatus.PUBLISHED) {
                response = await unpublishPolicy(policy.id)
                if (response.success) {
                    toast.success('Política despublicada com sucesso')
                } else {
                    toast.error(
                        response.error || 'Erro ao despublicar política'
                    )
                }
            } else {
                response = await publishPolicy(policy.id)
                if (response.success) {
                    toast.success('Política publicada com sucesso')
                } else {
                    toast.error(response.error || 'Erro ao publicar política')
                }
            }

            if (response.success) {
                await fetchPolicies(currentPage)
            }
        } catch (error) {
            console.error('Erro ao alterar status da política:', error)
            toast.error('Erro ao alterar status da política')
        } finally {
            setPublishing(null)
        }
    }

    const handleDeleteClick = (policy: Policy) => {
        setSelectedPolicy(policy)
        setShowDeleteModal(true)
    }

    const handleDeleteConfirm = async () => {
        if (!selectedPolicy) return

        try {
            setDeleting(true)
            const response = await deletePolicy(selectedPolicy.id)

            if (response.success) {
                await fetchPolicies(currentPage)
                toast.success('Política removida com sucesso')
                setShowDeleteModal(false)
                setSelectedPolicy(null)
            } else {
                toast.error(response.error || 'Erro ao remover política')
            }
        } catch (error) {
            console.error('Erro ao remover política:', error)
            toast.error('Erro ao remover política')
        } finally {
            setDeleting(false)
        }
    }

    const handlePolicyTypeSelect = (type: PolicyType) => {
        navigate(`/policies/new?type=${type}`)
    }

    const handleSendToAnalysisClick = (policy: Policy) => {
        setSelectedPolicy(policy)
        setShowAnalysisModal(true)
    }

    const handleSendToAnalysisConfirm = async () => {
        if (!selectedPolicy) return

        try {
            setSendingToAnalysis(true)
            const response = await sendToAnalysis(selectedPolicy.id)

            if (response.success) {
                toast.success('Política enviada para revisão com sucesso')
                await fetchPolicies(currentPage)
                setShowAnalysisModal(false)
                setSelectedPolicy(null)
            } else {
                toast.error(
                    response.error || 'Erro ao enviar política para revisão'
                )
            }
        } catch (error) {
            console.error('Erro ao enviar política para revisão:', error)
            toast.error('Erro ao enviar política para revisão')
        } finally {
            setSendingToAnalysis(false)
        }
    }

    const handlePreview = (policy: Policy) => {
        const htmlContent = `
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Preview - ${getPolicyTypeLabel(policy.type)}</title>
                    <meta charset="utf-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1">
                    <style>
                        body {
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                            max-width: 800px;
                            margin: 0 auto;
                            padding: 40px 20px;
                            line-height: 1.6;
                            color: #333;
                        }
                        h1, h2, h3, h4, h5, h6 { color: #2563eb; margin-top: 2em; margin-bottom: 0.5em; }
                        h1 { border-bottom: 2px solid #e5e7eb; padding-bottom: 0.5em; margin-bottom: 1em; }
                        p { margin-bottom: 1em; }
                        ul, ol { margin-bottom: 1em; padding-left: 2em; }
                        blockquote { border-left: 4px solid #e5e7eb; margin: 1em 0; padding: 0 1em; color: #6b7280; }
                        table { width: 100%; border-collapse: collapse; margin: 1em 0; }
                        th, td { border: 1px solid #e5e7eb; padding: 0.5em 1em; text-align: left; }
                        th { background: #f9fafb; font-weight: 600; }
                    </style>
                </head>
                <body>
                    <p style="color: #6b7280; font-size: 0.9em; margin-bottom: 2em;">
                        Versão ${formatVersion(policy.version)} | ${formatDate(policy.updatedAt)}
                    </p>
                    ${policy.content}
                </body>
            </html>
        `

        setPreviewContent(htmlContent)
        setSelectedPreviewPolicy(policy)
        setShowPreviewModal(true)
    }

    return (
        <div>
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">
                    Políticas e Termos
                </h1>

                <div className="flex items-center gap-4">
                    <form onSubmit={handleSearch} className="flex-1 relative">
                        <div className="relative">
                            <MdSearch className="absolute z-10 left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Pesquisar políticas..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 lg:min-w-[300px] py-3 border-0 rounded-lg focus:outline-none focus:ring-2 shadow-none focus:ring-neutral focus:border-transparent bg-[#F1F7FC]/50"
                            />
                        </div>
                    </form>

                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowPolicyTypeModal(true)}
                    >
                        <MdAdd className="text-lg" /> Adicionar nova
                    </button>
                </div>
            </div>

            <div className="mt-10">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600">
                            Carregando políticas...
                        </p>
                    </div>
                ) : (
                    <table className="table-non-zebra w-full">
                        <thead>
                            <tr>
                                <th>Tipo de documento</th>
                                <th>Versão</th>
                                <th>Última modificação</th>
                                <th>Status</th>
                                <th>Publicar</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {policies.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="text-center py-8 text-gray-500"
                                    >
                                        Nenhuma política encontrada
                                    </td>
                                </tr>
                            ) : (
                                policies.map(policy => (
                                    <tr key={policy.id}>
                                        <td className="font-medium">
                                            {getPolicyTypeLabel(policy.type)}
                                        </td>
                                        <td>{formatVersion(policy.version)}</td>
                                        <td>{formatDate(policy.updatedAt)}</td>
                                        <td>{getStatusBadge(policy.status)}</td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className="toggle toggle-sm toggle-secondary"
                                                checked={
                                                    policy.status ===
                                                    PolicyStatus.PUBLISHED
                                                }
                                                onChange={() =>
                                                    handlePublishToggle(policy)
                                                }
                                                disabled={
                                                    publishing === policy.id
                                                }
                                            />
                                        </td>
                                        <td className="actions-cell">
                                            <div className="actions-cell-items">
                                                <button
                                                    className="btn btn-ghost btn-circle hover:bg-[#E3F0F9] !border-none hover:text-blue-500"
                                                    title="Ver detalhes"
                                                    onClick={() =>
                                                        handlePreview(policy)
                                                    }
                                                >
                                                    <CgEye className="text-xl" />
                                                </button>
                                                {policy.status ===
                                                    PolicyStatus.DRAFT && (
                                                    <button
                                                        className="btn btn-ghost btn-sm p-0 min-h-0 h-8 w-8 hover:bg-[#FFF4E5] hover:text-orange-500"
                                                        title="Enviar para revisão"
                                                        onClick={() =>
                                                            handleSendToAnalysisClick(
                                                                policy
                                                            )
                                                        }
                                                        disabled={
                                                            sendingToAnalysis
                                                        }
                                                    >
                                                        <MdSend className="text-base" />
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-ghost btn-sm p-0 min-h-0 h-8 w-8"
                                                    title="Editar"
                                                    onClick={() =>
                                                        navigate(
                                                            `/policies/${policy.id}`
                                                        )
                                                    }
                                                >
                                                    <GoPencil className="text-base" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setSelectedPolicy(null)
                }}
                onConfirm={handleDeleteConfirm}
                title="Remover política?"
                message="Tem certeza de que deseja remover esta política? Esta ação não pode ser desfeita."
                confirmButtonText="Remover"
                loading={deleting}
            />

            <ConfirmModal
                isOpen={showAnalysisModal}
                onClose={() => {
                    setShowAnalysisModal(false)
                    setSelectedPolicy(null)
                }}
                onConfirm={handleSendToAnalysisConfirm}
                title="Enviar para revisão?"
                message={`Tem certeza de que deseja enviar a política "${
                    selectedPolicy
                        ? getPolicyTypeLabel(selectedPolicy.type)
                        : ''
                }" para revisão?`}
                confirmButtonText="Enviar para revisão"
                loading={sendingToAnalysis}
                confirmButtonClass="btn btn-warning"
            />

            <PolicyTypeModal
                isOpen={showPolicyTypeModal}
                onClose={() => setShowPolicyTypeModal(false)}
                onSelect={handlePolicyTypeSelect}
            />

            {/* Modal de Preview */}
            <div className={`modal ${showPreviewModal ? 'modal-open' : ''}`}>
                <div className="modal-box w-11/12 max-w-5xl h-[85vh] p-0 flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
                        <h3 className="text-lg font-bold">
                            {selectedPreviewPolicy
                                ? getPolicyTypeLabel(selectedPreviewPolicy.type)
                                : ''}
                        </h3>
                        <button
                            className="btn btn-sm btn-circle btn-ghost"
                            onClick={() => setShowPreviewModal(false)}
                        >
                            X
                        </button>
                    </div>
                    <div className="flex-1 overflow-hidden">
                        <iframe
                            srcDoc={previewContent}
                            className="w-full h-full border-0"
                            title="Preview da Política"
                            sandbox="allow-same-origin"
                        />
                    </div>
                </div>
                <div
                    className="modal-backdrop"
                    onClick={() => setShowPreviewModal(false)}
                />
            </div>
        </div>
    )
}
